/**
 * Task pieces the pages and threads share: the status label with its Ongoing
 * mark, a thread header's task chip, and the task card in a thread (slice 2b
 * spec §5, §10).
 */
import { StatusBadge, StatusDot, StatusLabel } from "@crew/ui/components/status";
import { TaskCard } from "@crew/ui/components/thread";
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import { ArrowUpRight, Repeat } from "lucide-react";
import type { TaskItem } from "./api";
import { isOpen, taskStatus, threadTasksHref, threadTasksLabel } from "./task-view";

/** "Ongoing": healthy while quiet, so the watch and follow-through leave it alone. */
function OngoingMark({ className }: { className?: string }) {
  return (
    <span
      data-ongoing
      title="Healthy while quiet: no check-ins"
      className={cn("inline-flex items-center gap-1 text-xs text-muted-foreground", className)}
    >
      <Repeat aria-hidden className="size-3" />
      Ongoing
    </span>
  );
}

/** Working, Queued, Waiting on <text> (hollow), Stuck, Done, Canceled; the Ongoing mark beside it. */
export function TaskStatus({
  task,
  viewerId,
  emphasis = false,
  className,
}: {
  task: Pick<TaskItem, "stage" | "waiting_on_text" | "ongoing" | "waiting_on_user_id">;
  /** The signed-in person: a task waiting on them reads "Waiting on you". */
  viewerId?: string;
  emphasis?: boolean;
  className?: string;
}) {
  const s = taskStatus(task, viewerId);
  return (
    <span
      data-task-status={task.stage}
      className={cn("inline-flex flex-wrap items-center gap-x-2.5 gap-y-1", className)}
    >
      <StatusLabel tone={s.tone} hollow={s.hollow} emphasis={emphasis && !s.hollow}>
        {s.label}
      </StatusLabel>
      {task.ongoing && isOpen(task.stage) ? <OngoingMark /> : null}
    </span>
  );
}

/** A thread header's tasks: "Task · Working" for one open task, "2 tasks" for more. */
export function ThreadTasksChip({ tasks }: { tasks: TaskItem[] | undefined }) {
  const label = threadTasksLabel(tasks);
  if (!label) return null;
  const open = (tasks ?? []).filter((t) => isOpen(t.stage));
  const tone = open.length === 1 ? taskStatus(open[0]) : null;
  return (
    <StatusBadge
      data-thread-tasks
      render={<Link href={threadTasksHref(tasks)} />}
      className="h-7 max-w-[55%] min-w-0 gap-1.5 rounded-md border-border bg-card px-2.5 text-xs font-medium text-copy shadow-ring"
    >
      {tone ? <StatusDot tone={tone.tone} hollow={tone.hollow} /> : null}
      <span className="truncate">{label}</span>
      <ArrowUpRight className="shrink-0 text-muted-foreground" />
    </StatusBadge>
  );
}

/**
 * A `task_card` in a thread: live from the thread's `tasks`, its stage, who
 * it's for and its latest note. A person's card (New task) says so, and who asked.
 */
export function ThreadTaskCard({
  task,
  fallbackTitle,
  askedBy,
  size,
}: {
  task: TaskItem | undefined;
  /** The message's body, until the task loads. */
  fallbackTitle: string;
  /** A person's card: their first name. */
  askedBy: string | null;
  size: "md" | "sm";
}) {
  const eyebrow = askedBy ? `New task · from ${askedBy}` : undefined;
  if (!task) {
    return <TaskCard size={size} href="/tasks" title={fallbackTitle} eyebrow={eyebrow} />;
  }
  const forName = task.for?.name.split(/\s+/)[0];
  return (
    <TaskCard
      size={size}
      href={`/tasks/${task.id}`}
      title={task.title}
      eyebrow={eyebrow}
      status={<TaskStatus task={task} className="text-xs" />}
      meta={forName ? `For ${forName}` : undefined}
      detail={task.detail || undefined}
    />
  );
}
